import { useState, useMemo, useCallback } from 'react';

import { useQuery } from '@zenbusiness/application-commons-graphql';

import { PAGINATION_DEFAULT_OFFSET } from '#client/constants/pagination';
import { TOKEN_TYPES } from '#client/constants/tokens';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCustomerToken from '#client/hooks/useCustomerToken';
import { useLogError } from '#client/hooks/useLogError';

import { GET_STATEMENTS_QUERY } from '../queries/statement';

const PAGINATION_LIMIT = 12;

const useGetStatements = () => {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  // set a default offset of 0 to load the first page
  const [offset, setOffset] = useState(PAGINATION_DEFAULT_OFFSET);
  const [isLoadMore, setIsLoadMore] = useState(false);
  const { customerToken, loading: loadingToken } = useCustomerToken({
    type: TOKEN_TYPES.COMMON,
    prefetch: true
  });

  const handleLoadMore = useCallback(() => {
    setIsLoadMore(true);
    setOffset(offset + PAGINATION_LIMIT);
  }, [offset]);

  const updateOriginalStatements = (prevData, data) => {
    const statements = isLoadMore
      ? {
          statementListItems: {
            data: [...prevData.statementListItems.data, ...data.statementListItems.data],
            pagination: data.statementListItems.pagination
          }
        }
      : data;
    setIsLoadMore(false);

    return statements;
  };

  const { loading, data, error, errors } = useQuery(GET_STATEMENTS_QUERY, {
    variables: {
      params: {
        businessUuid: businessEntityUuid,
        customerToken,
        offset,
        limit: PAGINATION_LIMIT
      }
    },
    updateData: updateOriginalStatements,
    skipCache: true,
    noThrow: true
  });

  useLogError(errors, error);

  const hasNextPagination = useMemo(
    () => Boolean(data?.statementListItems?.pagination?.next),
    [data]
  );

  return {
    data,
    loading,
    loadingToken,
    hasNextPagination,
    error,
    isLoadMore,
    handleLoadMore
  };
};

export default useGetStatements;
